import { useState, useEffect, useCallback, useRef } from "react";

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  // Use ref to avoid recreating initialValue dependency
  const initialValueRef = useRef(initialValue);
  
  // Initialize state with a function to read from localStorage only once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValueRef.current;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValueRef.current;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValueRef.current;
    }
  });

  // Stable setValue that uses functional update to avoid stale closure
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        setStoredValue(prev => {
          const valueToStore = value instanceof Function ? value(prev) : value;
          if (typeof window !== "undefined") {
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
          }
          return valueToStore;
        });
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );

  // Listen for changes to this key in other tabs/windows — debounced to
  // avoid excessive JSON.parse calls on rapid cross-tab storage events.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== key || e.newValue === null) return;
      const newValue = e.newValue;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          setStoredValue(JSON.parse(newValue));
        } catch (error) {
          console.warn(`Error parsing localStorage change for key "${key}":`, error);
        }
      }, 100);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      if (timer) clearTimeout(timer);
    };
  }, [key]);

  return [storedValue, setValue];
}
